import classNames from 'classnames';
import { memo } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';

export type WorkspaceAgentTablePillFooterProps = {
  captionId: string;
  fetchErrorLabel: string;
  hasMore: boolean;
  isFetchNextPageError: boolean;
  isNextPageDisabled: boolean;
  isPreviousPageDisabled: boolean;
  loadingMoreLabel: string;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onRetryLoadMore?: () => void;
  pageCount: number;
  rangeLabel: string;
};

export const WorkspaceAgentTablePillFooter = memo(
  ({
    captionId,
    fetchErrorLabel,
    hasMore,
    isFetchNextPageError,
    isNextPageDisabled,
    isPreviousPageDisabled,
    loadingMoreLabel,
    onNextPage,
    onPreviousPage,
    onRetryLoadMore,
    pageCount,
    rangeLabel,
  }: WorkspaceAgentTablePillFooterProps) => (
    <div className="mt-2 flex items-center justify-between gap-2 ads:border-subtle border-gray-3 border-t px-2.5 pt-2 leading-4">
      <span className="flex min-w-0 items-center gap-1">
        <span aria-live="polite" className="min-w-0 ads:text-secondary text-10 text-gray-11" id={captionId}>
          {rangeLabel}
          {loadingMoreLabel}
          {fetchErrorLabel}
        </span>
        {isFetchNextPageError && onRetryLoadMore ? (
          <span className="shrink-0">
            <Button
              appearance="ghost"
              data-testid="WORKSPACE_AGENT_TABLE_PILL_RETRY_LOAD_MORE"
              onClick={onRetryLoadMore}
              size="small"
              type="button"
            >
              Retry
            </Button>
          </span>
        ) : null}
      </span>
      {pageCount > 1 || hasMore ? (
        <span className="flex shrink-0 items-center gap-0.5">
          <IconButton
            adsIcon
            appearance="ghost"
            aria-disabled={isPreviousPageDisabled}
            className={classNames(isPreviousPageDisabled && 'pointer-events-none opacity-40')}
            color="gray"
            data-testid="WORKSPACE_AGENT_TABLE_PILL_PREVIOUS"
            icon={ChevronLeftIcon}
            label="Previous page"
            onClick={onPreviousPage}
            size="extra-small"
          />
          <IconButton
            adsIcon
            appearance="ghost"
            aria-disabled={isNextPageDisabled}
            className={classNames(isNextPageDisabled && 'pointer-events-none opacity-40')}
            color="gray"
            data-testid="WORKSPACE_AGENT_TABLE_PILL_NEXT"
            icon={ChevronRightIcon}
            label="Next page"
            onClick={onNextPage}
            size="extra-small"
          />
        </span>
      ) : null}
    </div>
  ),
);

WorkspaceAgentTablePillFooter.displayName = 'WorkspaceAgentTablePillFooter';
